/**
 * AB#650 — a "blueprint" is one editable theme copy, owned by one of three scopes,
 * which are also the three columns:
 *
 *   Account  — shared with every seat of this site's account (`scope: "account"`)
 *   Site     — owned by this site alone (`scope: "site"`)
 *   User     — the caller's own themes, on every account (`scope: "user"`)
 *
 * All three are rows behind the SAME `/account/themes/{id}` file API, so a tile's
 * behaviour never depends on which column it sits in. The site's build uses the
 * starred one (`settings.account_theme_id`), else its bundled theme.
 */

import {
  accountThemesApi,
  sitesApi,
  themesApi,
  useThemeFile,
  useBundledThemeFile,
  useSaveThemeFile,
  useDeleteThemeFile,
  useAccountThemeFile,
  useSaveAccountThemeFile,
  useDeleteAccountThemeFile,
  type AccountTheme,
  type AccountThemeScope,
  type BundledTheme,
  type ThemeFile,
} from '@wordpuppi/shared';

/**
 * Which file API a blueprint's files are read/written through.
 *
 * ponytail: the `site` arm is unused now that site blueprints are ordinary
 * `/account/themes` rows — it stays because ThemeStyleEditor (not this module)
 * types against `FileSource`, and deleting it buys nothing. Drop both together.
 */
export type FileSource =
  | { scope: 'site'; siteSlug: string }
  | { scope: 'blueprint'; id: number }
  /** AB#644: a bundled ("stock") theme. Read-only — writes have no endpoint. */
  | { scope: 'stock'; themeId: string };

/** The columns are exactly the scopes. */
export type BlueprintColumn = AccountThemeScope;

/**
 * The `?bp=` key as a file source. AB#812 — the template designer is reached by
 * URL, so it only has the key, not the blueprint it came from.
 */
export function sourceFromKey(key: string | undefined, siteSlug: string): FileSource {
  const [scope, rest] = (key ?? '').split(/:(.*)/s);
  if (scope === 'stock' && rest) return { scope: 'stock', themeId: rest };
  const id = Number(rest);
  return Number.isFinite(id) && id > 0 ? { scope: 'blueprint', id } : { scope: 'site', siteSlug };
}

/** Read one file, whichever source the blueprint's files live behind. */
export function readSourceFile(source: FileSource, path: string) {
  switch (source.scope) {
    case 'blueprint':
      return accountThemesApi.getFile(source.id, path);
    case 'stock':
      return sitesApi.bundledThemeFile(source.themeId, path);
    default:
      return themesApi.getFile(source.siteSlug, path);
  }
}

/** Which theme a preview build/page request should render (AB#644 stock included). */
export function previewThemeArgs(source: FileSource): { preview_theme_id?: number; preview_theme?: string } {
  if (source.scope === 'blueprint') return { preview_theme_id: source.id };
  return source.scope === 'stock' ? { preview_theme: source.themeId } : {};
}

export interface Blueprint {
  /** URL value for `?bp=` — `<scope>:<id>`, or `stock:<theme id>` (AB#644). */
  key: string;
  column: BlueprintColumn;
  name: string;
  files: ThemeFile[];
  /**
   * Paths inherited from the theme this was copied from ("Standard files") —
   * EDITED OR NOT. AB#845: editing one used to demote it to Custom, which read
   * as "WordPuppi lost my standard file". Where it came from doesn't change
   * because you typed in it; the Standard list badges it "edited" instead.
   */
  originFiles: string[];
  source: FileSource;
  /** The row behind this tile — null for a stock theme, which has no row. */
  accountTheme: AccountTheme | null;
  /**
   * AB#644: set when this tile is a STOCK theme — one of the themes bundled with
   * WordPuppi. Stock tiles sit in the Account column for every account, are
   * read-only, and have no Delete (there is no row and no endpoint to delete).
   * Duplicate one to get an editable blueprint.
   */
  stock?: BundledTheme;
  /** One-line description — stock themes only (from their `theme.json`). */
  description?: string | null;
}

export const blueprintKey = (t: AccountTheme) => `${t.scope}:${t.id}`;

export const stockKey = (t: BundledTheme) => `stock:${t.id}`;

export function accountBlueprint(theme: AccountTheme): Blueprint {
  return {
    key: blueprintKey(theme),
    column: theme.scope,
    name: theme.name,
    files: theme.files ?? [],
    originFiles: theme.origin_files ?? [],
    source: { scope: 'blueprint', id: theme.id },
    accountTheme: theme,
  };
}

/**
 * A bundled theme as an Account-column tile. Every file is "Standard" (there is
 * nothing custom in a theme nobody can edit), so the Custom Files tab is empty
 * and the editor renders read-only.
 */
export function stockBlueprint(theme: BundledTheme): Blueprint {
  const files = theme.files ?? [];
  return {
    key: stockKey(theme),
    column: 'account',
    name: theme.name,
    files,
    originFiles: files.map((f) => f.path),
    source: { scope: 'stock', themeId: theme.id },
    accountTheme: null,
    stock: theme,
    description: theme.description,
  };
}

/**
 * The blueprint's main stylesheet — what the guided colors/fonts editor (AB#624)
 * and the tile swatch both read. `static/style.css` by convention, else the first
 * stylesheet under `static/`. null when the blueprint has none.
 */
export function styleFilePath(bp: Blueprint): string | null {
  const file =
    bp.files.find((f) => f.path === 'static/style.css') ??
    bp.files.find((f) => f.path.endsWith('.css') && f.path.startsWith('static/'));
  return file?.path ?? null;
}

/**
 * Split a blueprint's files into Standard (inherited from the origin theme) and
 * Custom (files the origin never had). AB#845 — origin membership decides it,
 * not whether the file has been edited.
 *
 * A fork whose origin is gone or is another site (`origin_files` empty) has no
 * Standard files at all, so everything lists as Custom. ponytail: no invented
 * fallback — the server is the only thing that knows what the origin held.
 */
export function splitFiles(bp: Blueprint): { standard: ThemeFile[]; custom: ThemeFile[] } {
  const origin = new Set(bp.originFiles);
  return {
    standard: bp.files.filter((f) => origin.has(f.path)),
    custom: bp.files.filter((f) => !origin.has(f.path)),
  };
}

// ---------------------------------------------------------------------------
// Source-agnostic file hooks. Both underlying hooks are always called (rules of
// hooks); the inactive one is disabled by its own `enabled` guard.
// ---------------------------------------------------------------------------

const blueprintId = (source: FileSource) => (source.scope === 'blueprint' ? source.id : null);

export function useSourceFile(source: FileSource, path: string | null) {
  const isSite = source.scope === 'site';
  const isStock = source.scope === 'stock';
  const site = useThemeFile(isSite ? source.siteSlug : '', isSite ? path : null);
  const blueprint = useAccountThemeFile(blueprintId(source), blueprintId(source) == null ? null : path);
  const stock = useBundledThemeFile(isStock ? source.themeId : null, isStock ? path : null);
  if (isSite) return site;
  return isStock ? stock : blueprint;
}

/**
 * Stock themes have no write endpoint, so their mutation is the (disabled)
 * blueprint one — the editor renders read-only for them and never fires it.
 */
export function useSourceSaveFile(source: FileSource) {
  const isSite = source.scope === 'site';
  const site = useSaveThemeFile(isSite ? source.siteSlug : '');
  const blueprint = useSaveAccountThemeFile(blueprintId(source));
  return isSite ? site : blueprint;
}

export function useSourceDeleteFile(source: FileSource) {
  const isSite = source.scope === 'site';
  const site = useDeleteThemeFile(isSite ? source.siteSlug : '');
  const blueprint = useDeleteAccountThemeFile(blueprintId(source));
  return isSite ? site : blueprint;
}

// ---------------------------------------------------------------------------
// Tile menu. Pure (no JSX) so the rules are unit-testable; BlueprintColumns
// renders the returned list into Base UI menu parts.
// ---------------------------------------------------------------------------

/** Where a Duplicate lands — same three scopes. */
export type DuplicateTarget = AccountThemeScope;

export interface TileActions {
  onDuplicate: (bp: Blueprint, target: DuplicateTarget) => void;
  onRename: (bp: Blueprint) => void;
  /** `next` = whether this blueprint should become the site default. */
  onSetDefault: (bp: Blueprint, next: boolean) => void;
  onDelete: (bp: Blueprint) => void;
}

export interface MenuItem {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Native tooltip — the only place a disabled item can explain itself. */
  title?: string;
  /** Rendered after a separator, in the destructive variant. */
  destructive?: boolean;
  /** Present = render as a submenu instead of a plain item. */
  items?: MenuItem[];
}

const duplicateItem = (bp: Blueprint, actions: TileActions): MenuItem => ({
  label: 'Duplicate',
  items: [
    { label: 'Account', onClick: () => actions.onDuplicate(bp, 'account') },
    { label: 'Site', onClick: () => actions.onDuplicate(bp, 'site') },
    { label: 'User', onClick: () => actions.onDuplicate(bp, 'user') },
  ],
});

/**
 * The tile's whole menu, in order: Duplicate ▸ · Rename · Set/Clear site default
 * · —— · Delete. Identical for every column — the scope only decides whether the
 * blueprint may become the site default.
 *
 * `applied` is `settings.account_theme_id === bp.accountTheme.id` — the star, and
 * the thing the backend 409s `in_use` on when you try to delete it.
 *
 * AB#644 — a STOCK tile is the exception: it ships with WordPuppi, so it has no
 * row to rename or delete. Its menu is Duplicate ▸ (to get an editable copy) and
 * "Use as site theme", which sets `settings.theme` rather than the star. That is
 * the whole of "impossible to delete": there is no Delete item and no endpoint
 * behind one.
 */
export function menuItems(bp: Blueprint, applied: boolean, actions: TileActions): MenuItem[] {
  if (bp.stock) {
    return [
      duplicateItem(bp, actions),
      {
        label: applied ? 'Site theme ✓' : 'Use as site theme',
        onClick: applied ? undefined : () => actions.onSetDefault(bp, true),
        disabled: applied,
        title: applied ? 'This site already builds with this theme' : undefined,
      },
    ];
  }

  const theme = bp.accountTheme!;
  // Both of these make the backend 409 `in_use` on DELETE; disable rather than
  // let the user fire a request we know fails.
  const blocked = applied
    ? 'site default'
    : theme.in_use_by > 0
      ? `in use by ${theme.in_use_by} site${theme.in_use_by === 1 ? '' : 's'}`
      : null;

  return [
    duplicateItem(bp, actions),
    { label: 'Rename', onClick: () => actions.onRename(bp) },
    // User blueprints can't be a site default (the backend 400s `scope_not_allowed`),
    // but one that somehow IS applied still gets Clear so it can be un-set.
    ...(applied
      ? [{ label: 'Clear site default', onClick: () => actions.onSetDefault(bp, false) }]
      : theme.scope === 'user'
        ? []
        : [{ label: 'Set as site default', onClick: () => actions.onSetDefault(bp, true) }]),
    {
      label: blocked ? `Delete — ${blocked}` : 'Delete',
      onClick: () => actions.onDelete(bp),
      disabled: !!blocked,
      destructive: true,
    },
  ];
}
